import React, { useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, Check, X, Plus, Trash2, ChevronDown, ChevronUp } from 'lucide-react';
import toast from 'react-hot-toast';
import type { ResumeRequest, PersonalInfo, Experience, Education, ParsedData } from '../types';
import { useAuth } from '../context/AuthContext';
import RichTextEditor from './RichTextEditor';
import ResumeImportButton from './ResumeImportButton';

export interface InlineSuggestion {
  id: string;
  section: 'summary' | 'experience' | 'skills';
  field: string;
  label: string;
  originalValue: string;
  suggestedValue: string;
  status: 'pending' | 'accepted' | 'dismissed';
  experienceIndex?: number;
}

type SectionKey = 'personal' | 'summary' | 'experience' | 'education' | 'skills';

interface InlineEditorProps {
  workingData: ResumeRequest;
  suggestions: InlineSuggestion[];
  activeSection: SectionKey;
  isEnhancing: boolean;
  onSetActiveSection: (s: SectionKey) => void;
  onUpdatePersonalInfo: (info: PersonalInfo) => void;
  onUpdateSummary: (s: string) => void;
  onUpdateExperience: (index: number, exp: Experience) => void;
  onAddExperience: () => void;
  onRemoveExperience: (index: number) => void;
  onUpdateEducation: (index: number, edu: Education) => void;
  onAddEducation: () => void;
  onRemoveEducation: (index: number) => void;
  onUpdateSkills: (skills: string[]) => void;
  onAcceptSuggestion: (id: string) => void;
  onDismissSuggestion: (id: string) => void;
  onEnrichSection: (section: SectionKey) => void;
  onImport: (data: ParsedData) => void;
}

const sectionTabs: { key: SectionKey; label: string }[] = [
  { key: 'personal', label: 'Personal' },
  { key: 'summary', label: 'Summary' },
  { key: 'experience', label: 'Experience' },
  { key: 'education', label: 'Education' },
  { key: 'skills', label: 'Skills' },
];

function SuggestionBubble({
  suggestion,
  onAccept,
  onDismiss,
}: {
  suggestion: InlineSuggestion;
  onAccept: () => void;
  onDismiss: () => void;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: 'auto' }}
      exit={{ opacity: 0, height: 0 }}
      className="overflow-hidden"
    >
      <div className="mt-2 p-3 rounded-btn bg-brand-50 border border-brand-200">
        <div className="flex items-start gap-2">
          <Sparkles className="w-3.5 h-3.5 text-brand-500 mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-[11px] font-semibold text-brand-600 uppercase tracking-wider mb-1">
              AI Suggestion
            </div>
            <p className="text-sm text-zinc-700 leading-relaxed whitespace-pre-wrap">
              {suggestion.suggestedValue}
            </p>
          </div>
        </div>
        <div className="flex gap-2 mt-2">
          <button
            className="h-7 px-3 bg-brand-500 hover:bg-brand-600 text-white text-xs font-medium rounded-btn transition-colors inline-flex items-center gap-1"
            onClick={onAccept}
          >
            <Check className="w-3 h-3" />
            Accept
          </button>
          <button
            className="h-7 px-3 border border-zinc-200 text-zinc-500 hover:text-zinc-700 hover:bg-zinc-50 text-xs font-medium rounded-btn transition-colors inline-flex items-center gap-1"
            onClick={onDismiss}
          >
            <X className="w-3 h-3" />
            Dismiss
          </button>
        </div>
      </div>
    </motion.div>
  );
}

const InlineEditor: React.FC<InlineEditorProps> = ({
  workingData,
  suggestions,
  activeSection,
  isEnhancing,
  onSetActiveSection,
  onUpdatePersonalInfo,
  onUpdateSummary,
  onUpdateExperience,
  onAddExperience,
  onRemoveExperience,
  onUpdateEducation,
  onAddEducation,
  onRemoveEducation,
  onUpdateSkills,
  onAcceptSuggestion,
  onDismissSuggestion,
  onEnrichSection,
  onImport,
}) => {
  const { user } = useAuth();
  const profileFullName = [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim();
  const profileEmail = user?.email || '';
  const pendingSuggestions = suggestions.filter(s => s.status === 'pending');

  const getSectionSuggestions = useCallback(
    (section: SectionKey): InlineSuggestion[] => {
      if (section === 'personal') return [];
      return pendingSuggestions.filter(s => s.section === section);
    },
    [pendingSuggestions]
  );

  const handlePersonalChange = useCallback(
    (field: keyof PersonalInfo, value: string) => {
      onUpdatePersonalInfo({ ...workingData.personalInfo, [field]: value });
    },
    [workingData.personalInfo, onUpdatePersonalInfo]
  );

  const handleSkillRemove = useCallback(
    (skill: string) => {
      onUpdateSkills(workingData.skills.filter(s => s !== skill));
    },
    [workingData.skills, onUpdateSkills]
  );

  const renderSection = () => {
    switch (activeSection) {
      case 'personal':
        return (
          <div className="space-y-5">
            <div className="grid sm:grid-cols-2 gap-4">
              {([
                { label: 'Full Name', key: 'fullName' as const, placeholder: 'John Doe', readonly: true },
                { label: 'Email', key: 'email' as const, placeholder: 'john@example.com', readonly: true },
                { label: 'Phone', key: 'phone' as const, placeholder: '+1 234 567 890', readonly: false },
                { label: 'LinkedIn', key: 'linkedin' as const, placeholder: 'linkedin.com/in/johndoe', readonly: false },
                { label: 'Website', key: 'website' as const, placeholder: 'johndoe.com', readonly: false },
                { label: 'Address', key: 'address' as const, placeholder: 'City, Country', readonly: false },
              ] as const).map(({ label, key, placeholder, readonly }) => (
                <div key={key}>
                  <label className="block text-xs font-medium text-zinc-500 mb-1">{label}</label>
                  <input
                    type="text"
                    className={`w-full h-10 px-3 text-sm border border-zinc-200 rounded-btn bg-white text-foreground placeholder:text-zinc-300 focus:outline-none focus:border-brand-500 focus:ring-3 focus:ring-brand-100 ${
                      readonly ? 'bg-zinc-50 text-zinc-500 cursor-not-allowed' : ''
                    }`}
                    placeholder={placeholder}
                    value={readonly ? (key === 'fullName' ? profileFullName : profileEmail) : workingData.personalInfo[key] || ''}
                    onChange={readonly ? undefined : (e) => handlePersonalChange(key, e.target.value)}
                    readOnly={readonly}
                    title={readonly ? 'Managed in your profile' : undefined}
                  />
                </div>
              ))}
            </div>
          </div>
        );

      case 'summary':
        return (
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-xs font-medium text-zinc-500">Professional Summary</label>
              <button
                className="h-7 px-2.5 text-xs text-brand-500 hover:bg-brand-50 rounded-btn transition-colors inline-flex items-center gap-1 font-medium"
                onClick={() => onEnrichSection('summary')}
                disabled={isEnhancing}
              >
                <Sparkles className="w-3 h-3" />
                {isEnhancing ? '...' : 'Refine'}
              </button>
            </div>
            <RichTextEditor
              value={workingData.summary || ''}
              onChange={onUpdateSummary}
              placeholder="Write a brief professional summary..."
              minHeight="120px"
            />
            <AnimatePresence>
              {getSectionSuggestions('summary').map((s) => (
                <SuggestionBubble
                  key={s.id}
                  suggestion={s}
                  onAccept={() => onAcceptSuggestion(s.id)}
                  onDismiss={() => onDismissSuggestion(s.id)}
                />
              ))}
            </AnimatePresence>
          </div>
        );

      case 'experience':
        return (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-zinc-500">
                {workingData.experience.length} entr{workingData.experience.length === 1 ? 'y' : 'ies'}
              </span>
              <button
                className="h-7 px-2.5 text-xs text-brand-500 hover:bg-brand-50 rounded-btn transition-colors inline-flex items-center gap-1 font-medium"
                onClick={() => onEnrichSection('experience')}
                disabled={isEnhancing}
              >
                <Sparkles className="w-3 h-3" />
                {isEnhancing ? '...' : 'Refine All'}
              </button>
            </div>

            {workingData.experience.length === 0 && (
              <div className="text-center py-8 text-zinc-400 text-sm">
                No experience entries yet.
              </div>
            )}

            {workingData.experience.map((exp, i) => (
              <ExperienceCard
                key={i}
                exp={exp}
                index={i}
                suggestions={getSectionSuggestions('experience').filter(s => s.experienceIndex === i)}
                onChange={(updated) => onUpdateExperience(i, updated)}
                onRemove={() => onRemoveExperience(i)}
                onAcceptSuggestion={(id) => onAcceptSuggestion(id)}
                onDismissSuggestion={(id) => onDismissSuggestion(id)}
              />
            ))}

            <button
              className="w-full h-10 border-2 border-dashed border-zinc-200 rounded-btn text-zinc-400 hover:text-brand-500 hover:border-brand-300 text-sm font-medium transition-colors inline-flex items-center justify-center gap-1.5"
              onClick={onAddExperience}
            >
              <Plus className="w-4 h-4" />
              Add Experience
            </button>
          </div>
        );

      case 'education':
        return (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-zinc-500">
                {workingData.education.length} entr{workingData.education.length === 1 ? 'y' : 'ies'}
              </span>
              <button
                className="h-7 px-2.5 text-xs text-brand-500 hover:bg-brand-50 rounded-btn transition-colors inline-flex items-center gap-1 font-medium"
                onClick={() => onEnrichSection('education')}
                disabled={isEnhancing}
              >
                <Sparkles className="w-3 h-3" />
                {isEnhancing ? '...' : 'Refine'}
              </button>
            </div>

            {workingData.education.length === 0 && (
              <div className="text-center py-8 text-zinc-400 text-sm">
                No education entries yet.
              </div>
            )}

            {workingData.education.map((edu, i) => (
              <EducationCard
                key={i}
                edu={edu}
                index={i}
                onChange={(updated) => onUpdateEducation(i, updated)}
                onRemove={() => onRemoveEducation(i)}
              />
            ))}

            <button
              className="w-full h-10 border-2 border-dashed border-zinc-200 rounded-btn text-zinc-400 hover:text-brand-500 hover:border-brand-300 text-sm font-medium transition-colors inline-flex items-center justify-center gap-1.5"
              onClick={onAddEducation}
            >
              <Plus className="w-4 h-4" />
              Add Education
            </button>
          </div>
        );

      case 'skills':
        return (
          <div>
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-medium text-zinc-500">
                {workingData.skills.length} skills
              </span>
              <button
                className="h-7 px-2.5 text-xs text-brand-500 hover:bg-brand-50 rounded-btn transition-colors inline-flex items-center gap-1 font-medium"
                onClick={() => onEnrichSection('skills')}
                disabled={isEnhancing}
              >
                <Sparkles className="w-3 h-3" />
                {isEnhancing ? '...' : 'Refine'}
              </button>
            </div>

            <div className="flex flex-wrap gap-2 mb-4">
              {workingData.skills.map((skill) => (
                <span
                  key={skill}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium bg-zinc-100 text-zinc-700 border border-zinc-200"
                >
                  {skill}
                  <button
                    className="w-3.5 h-3.5 flex items-center justify-center rounded-full hover:bg-zinc-200 text-zinc-400 hover:text-zinc-600 transition-colors"
                    onClick={() => handleSkillRemove(skill)}
                  >
                    <X className="w-2.5 h-2.5" />
                  </button>
                </span>
              ))}
            </div>

            <SkillInput existingSkills={workingData.skills} onAdd={(skill) => onUpdateSkills([...workingData.skills, skill])} />

            <AnimatePresence>
              {getSectionSuggestions('skills').map((s) => (
                <SuggestionBubble
                  key={s.id}
                  suggestion={s}
                  onAccept={() => onAcceptSuggestion(s.id)}
                  onDismiss={() => onDismissSuggestion(s.id)}
                />
              ))}
            </AnimatePresence>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Section tabs */}
      <div className="flex gap-1 px-4 pt-4 pb-2 overflow-x-auto shrink-0 border-b border-zinc-100">
        {sectionTabs.map((tab) => {
          const sectionPendings = getSectionSuggestions(tab.key);
          return (
            <button
              key={tab.key}
              className={`relative h-9 px-3 text-sm font-medium rounded-btn transition-colors whitespace-nowrap ${
                activeSection === tab.key
                  ? 'bg-brand-50 text-brand-600'
                  : 'text-zinc-500 hover:text-zinc-700 hover:bg-zinc-50'
              }`}
              onClick={() => onSetActiveSection(tab.key)}
            >
              {tab.label}
              {sectionPendings.length > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-brand-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center">
                  {sectionPendings.length}
                </span>
              )}
            </button>
          );
        })}
        <div className="ml-auto flex items-center shrink-0">
          <ResumeImportButton onImported={onImport} />
        </div>
      </div>

      {/* Editor content */}
      <div className="flex-1 overflow-y-auto px-4 py-4">
        <AnimatePresence mode="wait">
          <motion.div
            key={activeSection}
            initial={{ opacity: 0, x: 8 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -8 }}
            transition={{ duration: 0.15 }}
          >
            {renderSection()}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};

// --- Sub-components ---

function ExperienceCard({
  exp,
  index,
  suggestions,
  onChange,
  onRemove,
  onAcceptSuggestion,
  onDismissSuggestion,
}: {
  exp: Experience;
  index: number;
  suggestions: InlineSuggestion[];
  onChange: (exp: Experience) => void;
  onRemove: () => void;
  onAcceptSuggestion: (id: string) => void;
  onDismissSuggestion: (id: string) => void;
}) {
  const [expanded, setExpanded] = React.useState(true);

  const update = (field: keyof Experience, value: string | boolean) => {
    onChange({ ...exp, [field]: value });
  };

  return (
    <div className="border border-zinc-200 rounded-card overflow-hidden bg-white">
      <div
        className="flex items-center gap-3 px-4 py-3 bg-zinc-50 cursor-pointer select-none"
        onClick={() => setExpanded(!expanded)}
      >
        <span className="text-xs font-semibold text-zinc-500 min-w-[20px]">{index + 1}.</span>
        <span className="text-sm font-medium text-foreground flex-1 truncate">
          {exp.role || 'New Role'}
        </span>
        {expanded ? (
          <ChevronUp className="w-4 h-4 text-zinc-400" />
        ) : (
          <ChevronDown className="w-4 h-4 text-zinc-400" />
        )}
      </div>

      {expanded && (
        <div className="p-4 border-t border-zinc-100 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[11px] font-medium text-zinc-400 mb-1">Company</label>
              <input
                type="text"
                className="w-full h-9 px-3 text-sm border border-zinc-200 rounded-btn bg-white text-foreground placeholder:text-zinc-300 focus:outline-none focus:border-brand-500 focus:ring-3 focus:ring-brand-100"
                placeholder="Company name"
                value={exp.company}
                onChange={(e) => update('company', e.target.value)}
              />
            </div>
            <div>
              <label className="block text-[11px] font-medium text-zinc-400 mb-1">Role</label>
              <input
                type="text"
                className="w-full h-9 px-3 text-sm border border-zinc-200 rounded-btn bg-white text-foreground placeholder:text-zinc-300 focus:outline-none focus:border-brand-500 focus:ring-3 focus:ring-brand-100"
                placeholder="Job title"
                value={exp.role}
                onChange={(e) => update('role', e.target.value)}
              />
            </div>
            <div>
              <label className="block text-[11px] font-medium text-zinc-400 mb-1">Start Date</label>
              <input
                type="text"
                className="w-full h-9 px-3 text-sm border border-zinc-200 rounded-btn bg-white text-foreground placeholder:text-zinc-300 focus:outline-none focus:border-brand-500 focus:ring-3 focus:ring-brand-100"
                placeholder="Jan 2020"
                value={exp.startDate}
                onChange={(e) => update('startDate', e.target.value)}
              />
            </div>
            <div>
              <label className="block text-[11px] font-medium text-zinc-400 mb-1">End Date</label>
              <input
                type="text"
                className="w-full h-9 px-3 text-sm border border-zinc-200 rounded-btn bg-white text-foreground placeholder:text-zinc-300 focus:outline-none focus:border-brand-500 focus:ring-3 focus:ring-brand-100"
                placeholder="Present"
                value={exp.endDate || ''}
                onChange={(e) => update('endDate', e.target.value)}
              />
            </div>
          </div>

          <div>
            <label className="block text-[11px] font-medium text-zinc-400 mb-1">Description</label>
            <RichTextEditor
              value={exp.description}
              onChange={(value) => update('description', value)}
              placeholder="Describe your responsibilities and achievements..."
              minHeight="80px"
            />
          </div>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={exp.current}
              onChange={(e) => update('current', e.target.checked)}
              className="w-4 h-4 rounded border-zinc-300 text-brand-500 focus:ring-brand-200"
            />
            <span className="text-xs text-zinc-500">I currently work here</span>
          </label>

          <AnimatePresence>
            {suggestions.map((s) => (
              <SuggestionBubble
                key={s.id}
                suggestion={s}
                onAccept={() => onAcceptSuggestion(s.id)}
                onDismiss={() => onDismissSuggestion(s.id)}
              />
            ))}
          </AnimatePresence>

          <div className="flex justify-end pt-1">
            <button
              className="h-7 px-2.5 text-xs text-red-500 hover:bg-red-50 rounded-btn transition-colors inline-flex items-center gap-1"
              onClick={onRemove}
            >
              <Trash2 className="w-3 h-3" />
              Remove
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function EducationCard({
  edu,
  index,
  onChange,
  onRemove,
}: {
  edu: Education;
  index: number;
  onChange: (edu: Education) => void;
  onRemove: () => void;
}) {
  const [expanded, setExpanded] = React.useState(true);

  const update = (field: keyof Education, value: string | boolean) => {
    const next = { ...edu, [field]: value };
    if (field === 'startDate' || field === 'endDate') {
      next.year = (next.endDate || next.startDate || '') as string;
    }
    onChange(next);
  };

  return (
    <div className="border border-zinc-200 rounded-card overflow-hidden bg-white">
      <div
        className="flex items-center gap-3 px-4 py-3 bg-zinc-50 cursor-pointer select-none"
        onClick={() => setExpanded(!expanded)}
      >
        <span className="text-xs font-semibold text-zinc-500 min-w-[20px]">{index + 1}.</span>
        <span className="text-sm font-medium text-foreground flex-1 truncate">
          {edu.institution || 'New Institution'}
        </span>
        {expanded ? (
          <ChevronUp className="w-4 h-4 text-zinc-400" />
        ) : (
          <ChevronDown className="w-4 h-4 text-zinc-400" />
        )}
      </div>

      {expanded && (
        <div className="p-4 border-t border-zinc-100 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="block text-[11px] font-medium text-zinc-400 mb-1">Institution</label>
              <input
                type="text"
                className="w-full h-9 px-3 text-sm border border-zinc-200 rounded-btn bg-white text-foreground placeholder:text-zinc-300 focus:outline-none focus:border-brand-500 focus:ring-3 focus:ring-brand-100"
                placeholder="University name"
                value={edu.institution}
                onChange={(e) => update('institution', e.target.value)}
              />
            </div>
            <div>
              <label className="block text-[11px] font-medium text-zinc-400 mb-1">Degree</label>
              <input
                type="text"
                className="w-full h-9 px-3 text-sm border border-zinc-200 rounded-btn bg-white text-foreground placeholder:text-zinc-300 focus:outline-none focus:border-brand-500 focus:ring-3 focus:ring-brand-100"
                placeholder="B.Sc. Computer Science"
                value={edu.degree}
                onChange={(e) => update('degree', e.target.value)}
              />
            </div>
            <div>
              <label className="block text-[11px] font-medium text-zinc-400 mb-1">Field of Study</label>
              <input
                type="text"
                className="w-full h-9 px-3 text-sm border border-zinc-200 rounded-btn bg-white text-foreground placeholder:text-zinc-300 focus:outline-none focus:border-brand-500 focus:ring-3 focus:ring-brand-100"
                placeholder="Computer Science"
                value={edu.fieldOfStudy}
                onChange={(e) => update('fieldOfStudy', e.target.value)}
              />
            </div>
            <div>
              <label className="block text-[11px] font-medium text-zinc-400 mb-1">GPA</label>
              <input
                type="text"
                className="w-full h-9 px-3 text-sm border border-zinc-200 rounded-btn bg-white text-foreground placeholder:text-zinc-300 focus:outline-none focus:border-brand-500 focus:ring-3 focus:ring-brand-100"
                placeholder="3.8"
                value={edu.gpa || ''}
                onChange={(e) => update('gpa', e.target.value)}
              />
            </div>
            <div>
              <label className="block text-[11px] font-medium text-zinc-400 mb-1">Start Date</label>
              <input
                type="text"
                className="w-full h-9 px-3 text-sm border border-zinc-200 rounded-btn bg-white text-foreground placeholder:text-zinc-300 focus:outline-none focus:border-brand-500 focus:ring-3 focus:ring-brand-100"
                placeholder="e.g. Sep 2016"
                value={edu.startDate || ''}
                onChange={(e) => update('startDate', e.target.value)}
              />
            </div>
            <div>
              <label className="block text-[11px] font-medium text-zinc-400 mb-1">End Date</label>
              <input
                type="text"
                className="w-full h-9 px-3 text-sm border border-zinc-200 rounded-btn bg-white text-foreground placeholder:text-zinc-300 focus:outline-none focus:border-brand-500 focus:ring-3 focus:ring-brand-100"
                placeholder="e.g. Jun 2020"
                value={edu.endDate || ''}
                onChange={(e) => update('endDate', e.target.value)}
              />
            </div>
          </div>

          <div className="flex justify-end pt-1">
            <button
              className="h-7 px-2.5 text-xs text-red-500 hover:bg-red-50 rounded-btn transition-colors inline-flex items-center gap-1"
              onClick={onRemove}
            >
              <Trash2 className="w-3 h-3" />
              Remove
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function SkillInput({ existingSkills, onAdd }: { existingSkills: string[]; onAdd: (skill: string) => void }) {
  const [value, setValue] = React.useState('');

  const handleAdd = () => {
    const trimmed = value.trim();
    if (!trimmed) return;
    if (existingSkills.includes(trimmed)) {
      toast.error('Skill already added', { id: 'skill-duplicate' });
      return;
    }
    onAdd(trimmed);
    setValue('');
  };

  return (
    <div className="flex gap-2">
      <input
        type="text"
        className="flex-1 h-10 px-3 text-sm border border-zinc-200 rounded-btn bg-white text-foreground placeholder:text-zinc-300 focus:outline-none focus:border-brand-500 focus:ring-3 focus:ring-brand-100"
        placeholder="Add a skill..."
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') handleAdd(); }}
      />
      <button
        className="h-10 px-4 bg-brand-500 hover:bg-brand-600 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium rounded-btn transition-colors inline-flex items-center gap-1.5"
        onClick={handleAdd}
        disabled={!value.trim()}
      >
        <Plus className="w-4 h-4" />
        Add
      </button>
    </div>
  );
}

export default InlineEditor;
